import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { NzModalService } from 'ng-zorro-antd/modal'
import { catchError, from, Observable, switchMap, throwError } from 'rxjs'
import { AppState } from '../core/reducers/index'
import { HTTP_RETRY_ATTEMPT } from './performance.context'

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  auth$ = this.store.select('auth')
  token?: string | undefined
  private visionRetryDialog?: Promise<boolean>

  constructor(private store: Store<AppState>, private modal: NzModalService) {
    this.token = localStorage.getItem('JWT_TOKEN') || undefined
    this.auth$.subscribe(res => {
      this.token = res.token
    })
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let modifiedReq: HttpRequest<unknown> | undefined
    if (this.token) {
      modifiedReq = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${this.token}`),
      })
    }
    const authorizedRequest = modifiedReq || request

    return next.handle(authorizedRequest).pipe(
      catchError(error => {
        if (!this.isVisionUnavailableError(error) || request.method !== 'GET') {
          return throwError(() => error)
        }

        const retryRequest = request.clone({
          context: request.context.set(HTTP_RETRY_ATTEMPT, request.context.get(HTTP_RETRY_ATTEMPT) + 1),
        })
        return from(this.confirmVisionRetry()).pipe(
          switchMap(retry => (retry ? this.intercept(retryRequest, next) : throwError(() => error))),
        )
      }),
    )
  }

  private confirmVisionRetry(): Promise<boolean> {
    if (this.visionRetryDialog) {
      return this.visionRetryDialog
    }

    this.visionRetryDialog = new Promise<boolean>(resolve => {
      this.modal.confirm({
        nzTitle: 'Vision není dostupný',
        nzContent: 'Systém Vision je dočasně nedostupný. Data se nepřepnou do offline režimu.',
        nzOkText: 'Načíst znovu',
        nzCancelText: 'Zavřít',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false),
      })
    }).finally(() => {
      this.visionRetryDialog = undefined
    })

    return this.visionRetryDialog
  }

  private isVisionUnavailableError(error: unknown): error is HttpErrorResponse {
    if (!(error instanceof HttpErrorResponse) || error.status !== 503) {
      return false
    }

    const message = typeof error.error === 'string' ? error.error : error.error?.message
    return typeof message === 'string' && message.toLowerCase().includes('vision')
  }
}
